import { Component, ElementRef, Input, OnChanges, OnInit, ViewChild } from '@angular/core';
import Chart from 'chart.js';
import moment from 'moment';
import { SessionData, SessionDataService } from '../../service/session-data.service';
import { SpinnerService } from '../../../spinner-component/spinner.service';

@Component({
  selector: 'app-session-bar-graph',
  templateUrl: './session-bar.component.html',
  styleUrls: ['../../user-behaviour.component.css']
,
  standalone: false
})
export class SessionBarComponent implements OnChanges {
  @Input() selectedTab: any;
  @ViewChild('sourceFrequencyChart', { static: false }) chartRef!: ElementRef;
  sessionData: SessionData[] = []; // Hold session data
  timeRange: string = 'month'; // Default time range
  loginFrequencyChart: any;
  sourceFrequencyChart: any;
  startDate: any = null;
  endDate: any = null;
  private isInitialLoad: boolean = true;
  loading =  true;

  constructor(private sessionDataService: SessionDataService, public spinner: SpinnerService) {}

  ngOnInit(): void {
    if (this.isInitialLoad && this.selectedTab == '0') {
      this.applyFilter();
      console.log("**ngOnInit session bar")
    }
  }

  ngOnChanges(changes: any): void {
    this.loading = false;
    if (!changes.selectedTab || String(changes.selectedTab.currentValue) !== '0') {
      return;
    }

    const previousTab = changes.selectedTab.previousValue;
    if (previousTab !== undefined && previousTab !== null && String(previousTab) !== '0') {
      this.resetDashboardFilters();
    }

    this.loading = true;
    this.applyFilter();
    this.isInitialLoad = false;
  }

  resetDashboardFilters(): void {
    this.timeRange = 'month';
    this.startDate = null;
    this.endDate = null;
  }

  updateGraphs(fromDate: string, toDate: string) {
    const payload = {
      fromDate: fromDate,
      toDate: toDate,
    };
    this.spinner.activate();
    this.sessionDataService.getHistoryGraphMetrics(payload).subscribe(data => {
      setTimeout(() => {
        this.loading = false;
        this.drawChart(data);
      }, 500);
      this.spinner.deactivate();
    });
  }

  filterAndCreateCharts() {
    let filteredData: SessionData[] = this.filterSessionsByTimeRange(this.sessionData);
    // this.createLoginFrequencyChart(filteredData);
    this.createSourceFrequencyChart(filteredData);
  }

  filterSessionsByTimeRange(data: any): SessionData[] {
    let filteredData: SessionData[] = [];
    if (this.timeRange === 'custom' && this.startDate && this.endDate) {
      filteredData = data?.content?.filter(session => {
        const sessionDate = moment(session.loginTimeInIST);
        return sessionDate.isBetween(this.startDate, this.endDate, 'day', '[]');
      });
    } else {
      let now = moment();
      filteredData = data?.content?.filter(session => {
        if (this.timeRange === 'day') {
          return moment(session.loginTimeInIST).isSame(now, 'day');
        } else if (this.timeRange === 'week') {
          return moment(session.loginTimeInIST).isSame(now, 'week');
        } else if (this.timeRange === 'month') {
          return moment(session.loginTimeInIST).isSame(now, 'month');
        }
        return false;
      });
    }

    return filteredData;
  }

  drawChart(sessionData: any) {
    const groupedData = sessionData?.groupedData;
    const dates = sessionData?.dates;
    const sources = sessionData?.sources;
    // console.log({dates,sources, groupedData})
    const datasets = sources.map((source, index) => {
      return {
        label: source,
        data: dates.map(date => groupedData[date]?.[source] || 0),
        backgroundColor: this.getColor(index)
      };
    });

    if (this.sourceFrequencyChart) {
      this.sourceFrequencyChart.destroy(); // Destroy previous chart instance if exists
    }

    //const ctx = this.chartRef.nativeElement.getContext('2d');
    const ctx = document.getElementById('sourceFrequencyChart') as HTMLCanvasElement;
    if (ctx) {
      this.sourceFrequencyChart = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: dates,
          datasets: datasets
        },
        options: {
          responsive: true,
          scales: {
            xAxes: [{
              stacked: true
            }],
            yAxes: [{
              stacked: true,
              ticks: {
                beginAtZero: true
              }
            }]
          }
        }
      });
    }
    //ctx.classList.remove('d-none');
  }

  applyFilter(): void {
    let fromDate: string;
    let toDate: string;
    const currentDate = new Date();  
    if (this.timeRange === 'day') {
      fromDate = this.formatDateToLocal(currentDate, '00:00:00');
      toDate = this.formatDateToLocal(currentDate, '23:59:59');
    } else if (this.timeRange === 'week') {
      const startOfWeek = new Date(currentDate);
      startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
      fromDate = this.formatDateToLocal(startOfWeek, '00:00:00');
      toDate = this.formatDateToLocal(currentDate, '23:59:59');
    } else if (this.timeRange === 'month') {
      const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      fromDate = this.formatDateToLocal(startOfMonth, '00:00:00');
      toDate = this.formatDateToLocal(currentDate, '23:59:59');
    } else if (this.timeRange === 'custom' && this.startDate && this.endDate) {
      fromDate = this.customFormatDate(this.startDate);
      toDate = this.customFormatDate(this.endDate);
    } 
    // else {
    //   fromDate = this.formatDateToLocal(currentDate, '00:00:00');
    //   toDate = this.formatDateToLocal(currentDate, '23:59:59');
    // }
    if (fromDate && toDate) {
      this.updateGraphs(fromDate, toDate);
    }
  }
  
  customFormatDate(momentDate: any): string {
    const formattedDate =  momentDate.format('YYYY-MM-DD HH:mm:ss');
    return formattedDate;
  }

  formatDate(date: Date): string {
    const localDateString = date.toLocaleDateString('en-US');
    return localDateString.split('T')[0] + ' 00:00:00';
  }

  formatDateToLocal(date: Date, prefix: string): string {
    const localDate = new Date(date.getTime());
    const year = localDate.getFullYear();
    const month = String(localDate.getMonth() + 1).padStart(2, '0');
    const day = String(localDate.getDate()).padStart(2, '0');
  
    const formattedDate = `${year}-${month}-${day} ${prefix}`;
  
    return formattedDate;
  }

  formatEndDate(date: Date): string {
    return date.toISOString().split('T')[0] + ' 23:59:59';
  }

  createLoginFrequencyChart(sessionData: SessionData[]) {
    const groupedData = this.groupByDate(sessionData);
    const dates = Object.keys(groupedData).sort();
    const counts = dates.map(date => groupedData[date]);

    if (this.loginFrequencyChart) {
      this.loginFrequencyChart.destroy(); // Destroy previous chart instance if exists
    }

    const ctx = document.getElementById('loginFrequencyChart') as HTMLCanvasElement;
    this.loginFrequencyChart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: dates,
        datasets: [{
          label: 'Logins',
          data: counts,
          backgroundColor: 'rgba(75, 192, 192, 0.2)',
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 1
        }]
      },
      options: {
        responsive: true,
        scales: {
          xAxes: [{
            stacked: true
          }],
          yAxes: [{
            stacked: true,
            ticks: {
              beginAtZero: true
            }
          }]
        }
      }
    });
  }

  createSourceFrequencyChart(sessionData: SessionData[]) {
    const groupedData = this.groupByDateAndSource(sessionData);
    const dates = Object.keys(groupedData).sort();
    const sources = Array.from(new Set(sessionData.map(session => session.source)));
    // console.log({dates,sources, groupedData})
    const datasets = sources.map((source, index) => {
      return {
        label: source,
        data: dates.map(date => groupedData[date][source] || 0),
        backgroundColor: this.getColor(index)
      };
    });

    if (this.sourceFrequencyChart) {
      this.sourceFrequencyChart.destroy(); // Destroy previous chart instance if exists
    }

    const ctx = document.getElementById('sourceFrequencyChart') as HTMLCanvasElement;
    this.sourceFrequencyChart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: dates,
        datasets: datasets
      },
      options: {
        responsive: true,
        scales: {
          xAxes: [{
            stacked: true
          }],
          yAxes: [{
            stacked: true,
            ticks: {
              beginAtZero: true
            }
          }]
        }
      }
    });
  }

  groupByDate(sessionData: SessionData[]): { [date: string]: number } {
    return sessionData.reduce((acc, session) => {
      const date = moment(session.loginTimeInIST).format('YYYY-MM-DD');
      if (!acc[date]) {
        acc[date] = 0;
      }
      acc[date]++;
      return acc;
    }, {});
  }

  groupByDateAndSource(sessionData: SessionData[]): { [date: string]: { [source: string]: number } } {
    return sessionData.reduce((acc, session) => {
      const date = moment(session.loginTimeInIST).format('YYYY-MM-DD');
      if (!acc[date]) {
        acc[date] = {};
      }
      if (!acc[date][session.source]) {
        acc[date][session.source] = 0;
      }
      acc[date][session.source]++;
      return acc;
    }, {});
  }

  getColor(index: number): string {
    const colors = [
      'rgba(75, 192, 192, 0.2)',
      'rgba(255, 99, 132, 0.2)',
      'rgba(54, 162, 235, 0.2)',
      'rgba(255, 206, 86, 0.2)',
      'rgba(153, 102, 255, 0.2)'
    ];
    return colors[index % colors.length];
  }

  onTimeRangeChange(event?: { value: string }) {
    if (event?.value) {
      this.timeRange = event.value;
    }
    if (this.timeRange !== 'custom') {
      this.applyFilter();
    }
  }
  
  onCustomDateChange() {
    if (this.timeRange === 'custom' && this.startDate && this.endDate) {
      this.applyFilter();
    }
  }
}
